import { supabase } from "../lib/supabase";
import type {
  SpeakerNotePage,
  SpeakerNotePageWithContent,
} from "./schema";

function toSpeakerNotePage(row: Record<string, unknown>): SpeakerNotePage {
  return {
    id: row.id as string,
    pdfId: row.pdf_id as string,
    title: row.title as string,
    order: row.order as number,
    createdAt: new Date(row.created_at as string),
    updatedAt: new Date(row.updated_at as string),
  };
}

/** page_id UNIQUE 제약 → Supabase가 단일 객체 또는 배열로 반환할 수 있음 */
function extractContent(
  rel: { content: string } | { content: string }[] | null,
): string {
  if (!rel) return "";
  if (Array.isArray(rel)) return rel[0]?.content ?? "";
  return rel.content;
}

export async function findManyNotePages(
  pdfId: string,
): Promise<SpeakerNotePage[]> {
  const { data, error } = await supabase
    .from("speaker_note_pages")
    .select("id, pdf_id, title, order, created_at, updated_at")
    .eq("pdf_id", pdfId)
    .order("order", { ascending: true });

  if (error) throw error;
  return (data ?? []).map((r) => toSpeakerNotePage(r as Record<string, unknown>));
}

export async function findOneNotePage(
  pageId: string,
): Promise<SpeakerNotePageWithContent> {
  const { data, error } = await supabase
    .from("speaker_note_pages")
    .select("id, pdf_id, title, order, created_at, updated_at, speaker_note_contents(content)")
    .eq("id", pageId)
    .single();

  if (error || !data) throw error ?? new Error("페이지를 찾을 수 없음");

  const row = data as Record<string, unknown> & {
    speaker_note_contents: { content: string } | { content: string }[] | null;
  };

  return { ...toSpeakerNotePage(row), content: extractContent(row.speaker_note_contents) };
}

export async function createNotePage(
  pdfId: string,
  order: number,
): Promise<SpeakerNotePage> {
  const { data: page, error: pageError } = await supabase
    .from("speaker_note_pages")
    .insert({ pdf_id: pdfId, title: `새 페이지 ${order + 1}`, order })
    .select()
    .single();

  if (pageError || !page) throw pageError;

  const { error: contentError } = await supabase
    .from("speaker_note_contents")
    .insert({ page_id: page.id, content: "" });

  if (contentError) throw contentError;

  return toSpeakerNotePage(page as Record<string, unknown>);
}

export async function updateNotePageTitle(
  pageId: string,
  title: string,
): Promise<void> {
  const { error } = await supabase
    .from("speaker_note_pages")
    .update({ title, updated_at: new Date().toISOString() })
    .eq("id", pageId);

  if (error) throw error;
}

export async function updateNoteContent(
  pageId: string,
  content: string,
): Promise<void> {
  const { data, error } = await supabase
    .from("speaker_note_contents")
    .upsert(
      { page_id: pageId, content, updated_at: new Date().toISOString() },
      { onConflict: "page_id" },
    )
    .select("page_id")
    .single();

  if (error) throw error;
  if (!data) throw new Error(`저장 실패: page_id=${pageId} (RLS 확인 필요)`);
}

export async function deleteNotePage(pageId: string): Promise<void> {
  const { error } = await supabase
    .from("speaker_note_pages")
    .delete()
    .eq("id", pageId);

  if (error) throw error;
}
